import { useEffect, useState } from 'react';
import type {
  ApiClient, SanitaerAbsicherungsliste, SanitaerAbsicherungszeile, SanitaerBerechnungsbereich,
  SanitaerSicherungseinrichtung,
} from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState, StatusBadge,
  Vorbehalte, formatiereZahl, herkunft, type ExportZiel, type Fachstatus, type Spalte, type Vorbehalt,
} from '../muster';
import { PageHeader } from '../rahmen/PageHeader';
import { Laufzeilen, pruefstand } from './SanitaerDruckzonen';
import { Rechenstand, zeitpunkt } from './sanitaer/kopf';
import './sanitaer/sanitaer.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABSICHERUNG NACH DIN EN 1717 (Paket TS9b2, TX-STANDARD S1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bisher nur als lesbarer Rückfall der Gattung `absicherung` auf
 * `sanitaer/trinkwasser` (die Gruppen). Hier die ganze Liste aus
 * `/sanitaer/absicherung`: je Gegenstand die Flüssigkeitskategorie, die
 * Soll-Sicherungsart und die 3-Liter-Regel (VDI 6023), daneben die Summen je
 * Sicherungsart und Kategorie - aus ihnen wird eine Bestellung; 2.938
 * Einzelzeilen beantworten die Frage „wie viele Systemtrenner“ nicht.
 *
 * Rechenstand, PDF und Excel sind die des Trinkwasserlaufs. Die Mappe trägt
 * nur die Gruppen; die Einzelliste gibt es als CSV aus der Tabelle (eine
 * Einzelliste in Excel bräuchte eine neue Route).
 *
 * SAN-07 (Katalog Gegenstandsart → Flüssigkeitskategorie nicht freigegeben)
 * steht NUR auf der Seite: Die Mappe ändert sich nicht.
 */

/** Nur auf der Seite, nicht in der Mappe. */
export const VORBEHALT_SAN07 = 'Vorbehalt SAN-07: Der Katalog Gegenstandsart → Flüssigkeitskategorie ist nicht '
  + 'freigegeben. Die Sicherungsarten folgen aus diesem Katalog; die Ist-Sicherungsart ist im Modell nicht gepflegt.';

export const HINWEIS_EINZELLISTE = 'Die Einzelliste steht nicht in der Mappe; CSV aus der Tabelle.';

const OHNE_TRINKWASSER = 'Für Trinkwasser liegt noch keine Berechnung vor.';

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

/** Die Bewertung je Gegenstand: erforderlich = Warnung (eine Sicherung fehlt bis zur Bestellung), ungeklärt = Warnung. */
export function absicherungsbewertung(bewertung: string): Fachstatus {
  switch (bewertung) {
    case 'erforderlich': return { zustand: 'warnung', text: 'Sicherung erforderlich' };
    case 'nicht_anwendbar': return { zustand: 'neutral', text: 'nicht anwendbar' };
    case 'ungeklaert': return { zustand: 'warnung', text: 'ungeklärt' };
    default: return { zustand: 'neutral', text: bewertung };
  }
}

function meldung(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function SanitaerAbsicherung({ api, projektId }: Eigenschaften) {
  const [liste, setListe] = useState<SanitaerAbsicherungsliste | null>(null);
  const [lauf, setLauf] = useState<{ daten: SanitaerBerechnungsbereich | null; ohne: string | null } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let aktiv = true;
    setListe(null);
    setLauf(null);
    setFehler(null);

    // Der Lauf nur für Kopf und Gruppen; ohne Lauf antwortet der Server mit „keine Berechnung“ - Stand, kein Fehler.
    api.sanitaerBerechnungBereich(projektId, 'trinkwasser', 'absicherung')
      .then((b) => { if (aktiv) setLauf({ daten: b, ohne: null }); })
      .catch((e) => { if (aktiv) setLauf({ daten: null, ohne: meldung(e) }); });

    api.sanitaerAbsicherung(projektId)
      .then((l) => { if (aktiv) setListe(l); })
      .catch((e) => { if (aktiv) setFehler(meldung(e)); });

    return () => { aktiv = false; };
  }, [api, projektId, versuch]);

  const laufDaten = lauf?.daten ?? null;
  const ohneLauf = laufDaten ? undefined : { grund: OHNE_TRINKWASSER };
  const ziele: ExportZiel[] = [
    {
      art: 'pdf', beschriftung: 'PDF Bericht', laden: () => api.sanitaerBerichtPdf(projektId, 'trinkwasser'),
      hinweis: 'Der Trinkwasserbericht (TWB).', gesperrt: ohneLauf,
    },
    {
      art: 'excel', beschriftung: 'Excel', laden: () => api.sanitaerBerechnungExcel(projektId, 'trinkwasser'),
      hinweis: `Die Trinkwassermappe mit dem Blatt Absicherung (Gruppen). ${HINWEIS_EINZELLISTE}`, gesperrt: ohneLauf,
    },
  ];

  const kopf = (
    <PageHeader
      titel="Absicherung nach DIN EN 1717"
      beschreibung={'Je Sanitärgegenstand Flüssigkeitskategorie, Sicherungsart und 3-Liter-Regel (VDI 6023) - mit den '
        + 'Summen je Sicherungsart und Kategorie für die Bestellung.'}
      meta={lauf ? <Rechenstand lauf={laufDaten?.lauf} veraltet={laufDaten?.lauf.standVeraltet} /> : undefined}
      aktionen={<ExportLeiste ziele={ziele} />}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Absicherung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((v) => v + 1)}
        />
      </>
    );
  }

  if (!liste || !lauf) {
    return <>{kopf}<LoadingState text="Absicherung wird abgerufen …" zeilen={10} spalten={7} /></>;
  }

  const vorbehalte: Vorbehalt[] = [{ art: 'fachfrage', text: VORBEHALT_SAN07, verweis: 'Katalog', ziel: '#absicherung-katalog' }];
  if (laufDaten?.lauf.standVeraltet) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Gerechnet am ${zeitpunkt(laufDaten.lauf.gerechnetAm)}, Sanitäraufnahme vom `
        + `${zeitpunkt(laufDaten.lauf.modellstand)}. Die Absicherung gilt für einen Modellstand, den es nicht mehr gibt.`,
    });
  }

  if (liste.zeilen.length === 0) {
    return (
      <>
        {kopf}
        <Vorbehalte eintraege={vorbehalte} />
        <EmptyState
          titel="Für dieses Projekt ist noch keine Absicherung gerechnet."
          erklaerung={'Die Absicherung entsteht mit dem Trinkwasserlauf über „Alle Bereiche rechnen“ auf der '
            + 'Bestandsseite.'}
          aktion={lauf.ohne ? <ErrorState klein meldung={lauf.ohne} /> : undefined}
        />
      </>
    );
  }

  return (
    <>
      {kopf}
      <Vorbehalte eintraege={vorbehalte} />

      <KpiReihe>
        <KpiCard titel="Gegenstände" wert={liste.gegenstaende} />
        <KpiCard
          titel="Sicherung erforderlich"
          wert={liste.erforderlich}
          zustand={liste.erforderlich > 0 ? 'warnung' : 'gut'}
          zustandText={liste.erforderlich > 0 ? 'zu bestellen' : 'keine'}
        />
        <KpiCard titel="nicht anwendbar" wert={liste.nichtAnwendbar} />
        <KpiCard
          titel="ungeklärt"
          wert={liste.ungeklaert}
          zustand={liste.ungeklaert > 0 ? 'warnung' : 'gut'}
          zustandText={liste.ungeklaert > 0 ? 'Kategorie offen' : 'alle eingestuft'}
        />
        <KpiCard
          titel="Stichleitung über 3 l"
          wert={liste.ueberDreiLiter}
          zustand={liste.ueberDreiLiter > 0 ? 'warnung' : 'gut'}
          zustandText={liste.ueberDreiLiter > 0 ? 'VDI 6023 verletzt' : 'eingehalten'}
        />
        <KpiCard titel="Inhalt der Stichleitungen" wert={liste.inhaltGesamtL} einheit="l" nachkomma={1} />
      </KpiReihe>

      <div className="san-zweispalter">
        <JeSicherungsart summen={liste.jeSicherungsart} />
        <JeKategorie summen={liste.jeKategorie} />
      </div>

      <Einzelliste zeilen={liste.zeilen} />
      <Katalog katalog={liste.katalog} />

      {laufDaten && (
        <Laufzeilen
          titel="Gruppen im Trinkwasserlauf (wie in der Mappe)"
          gattung="absicherung"
          zeilen={laufDaten.zeilen.filter((z) => z.gattung === 'absicherung')}
        />
      )}
    </>
  );
}

function JeSicherungsart({ summen }: { summen: SanitaerAbsicherungsliste['jeSicherungsart'] }) {
  const spalten: Spalte<SanitaerAbsicherungsliste['jeSicherungsart'][number]>[] = [
    { schluessel: 'sicherungsart', titel: 'Sicherungsart', immerSichtbar: true, wert: (s) => s.sicherungsart },
    { schluessel: 'zahl', titel: 'Gegenstände', art: 'zahl', wert: (s) => s.zahl, fuss: 'summe' },
  ];
  return (
    <ContentSection titel="Je Sicherungsart" nackt>
      <DataTable
        name="Je Sicherungsart"
        spalten={spalten}
        zeilen={summen}
        schluessel={(s) => s.sicherungsart}
        ausfuhr
        ausfuhrDateiname="sanitaer-absicherung-je-sicherungsart"
        dicht
        leer={<EmptyState klein titel="Kein Gegenstand braucht eine Sicherung." />}
      />
    </ContentSection>
  );
}

function JeKategorie({ summen }: { summen: SanitaerAbsicherungsliste['jeKategorie'] }) {
  const spalten: Spalte<SanitaerAbsicherungsliste['jeKategorie'][number]>[] = [
    {
      schluessel: 'kategorie', titel: 'Flüssigkeitskategorie', immerSichtbar: true,
      wert: (s) => s.kategorie, inhalt: (s) => <>Kategorie {formatiereZahl(s.kategorie, 0)}</>,
    },
    { schluessel: 'zahl', titel: 'Gegenstände', art: 'zahl', wert: (s) => s.zahl, fuss: 'summe' },
  ];
  return (
    <ContentSection titel="Je Flüssigkeitskategorie" nackt>
      <DataTable
        name="Je Flüssigkeitskategorie"
        spalten={spalten}
        zeilen={summen}
        schluessel={(s) => String(s.kategorie)}
        ausfuhr
        ausfuhrDateiname="sanitaer-absicherung-je-kategorie"
        dicht
        leer={<EmptyState klein titel="Kein Gegenstand ist eingestuft." />}
      />
    </ContentSection>
  );
}

/** Alle Zeilen, ohne Kappung - die erforderlichen und ungeklärten zuerst. */
function Einzelliste({ zeilen }: { zeilen: SanitaerAbsicherungszeile[] }) {
  const rang = (z: SanitaerAbsicherungszeile) => (z.bewertung === 'erforderlich' ? 0 : z.bewertung === 'ungeklaert' ? 1 : 2);
  const sortiert = zeilen.slice().sort((a, b) => rang(a) - rang(b));

  const spalten: Spalte<SanitaerAbsicherungszeile>[] = [
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', breite: '190px',
      wert: (z) => absicherungsbewertung(z.bewertung).text,
      inhalt: (z) => <StatusBadge {...absicherungsbewertung(z.bewertung)} klein />,
    },
    { schluessel: 'bezeichnung', titel: 'Gegenstand', immerSichtbar: true, wert: (z) => z.bezeichnung ?? z.art ?? '' },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'kategorie', titel: 'Kategorie', art: 'zahl', wert: (z) => z.fluessigkeitskategorie },
    {
      schluessel: 'soll', titel: 'Sicherungsart (Soll)', wert: (z) => z.sicherungsartSoll,
      inhalt: (z) => (z.sicherungsartSoll
        ? <>{z.sicherungsartSoll}{z.sicherungsartBezeichnung && <span className="schwach"> · {z.sicherungsartBezeichnung}</span>}</>
        : <>—</>),
    },
    {
      schluessel: 'ist', titel: 'Sicherungsart (Ist)', wert: (z) => z.sicherungsartIst ?? '',
      inhalt: (z) => (z.sicherungsartIst
        ? <>{z.sicherungsartIst}</>
        : <StatusBadge zustand="neutral" text="nicht gepflegt" klein />),
    },
    {
      schluessel: 'inhalt', titel: 'Stichleitung', art: 'zahl', einheit: 'l', nachkomma: 2,
      wert: (z) => z.stichleitungInhaltL,
      inhalt: (z) => (
        <>
          {formatiereZahl(z.stichleitungInhaltL, 2)}
          {z.stichleitungUeberGrenze && <> <StatusBadge zustand="warnung" text="über 3 l" klein /></>}
        </>
      ),
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
      wert: (z) => herkunft(z.herkunft).text,
      inhalt: (z) => <StatusBadge {...herkunft(z.herkunft)} klein />,
    },
    { schluessel: 'befund', titel: 'Befund', wert: (z) => z.befund ?? '' },
  ];

  return (
    <ContentSection
      titel="Gegenstände"
      meta={`${formatiereZahl(zeilen.length, 0)} Zeilen`}
      beschreibung={`Je Gegenstand die Einstufung; die erforderlichen und ungeklärten stehen oben. ${HINWEIS_EINZELLISTE}`}
      nackt
    >
      <DataTable
        name="Absicherung je Gegenstand"
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(z) => z.id}
        suche
        suchePlatzhalter="Gegenstand, Raum oder Sicherungsart suchen"
        ausfuhr
        ausfuhrDateiname="sanitaer-absicherung-gegenstaende"
        dicht
      />
    </ContentSection>
  );
}

function Katalog({ katalog }: { katalog: SanitaerSicherungseinrichtung[] }) {
  const spalten: Spalte<SanitaerSicherungseinrichtung>[] = [
    { schluessel: 'art', titel: 'Gegenstandsart', immerSichtbar: true, wert: (k) => k.bezeichnung || k.art },
    { schluessel: 'kategorie', titel: 'Kategorie', art: 'zahl', wert: (k) => k.fluessigkeitskategorie },
    {
      schluessel: 'sicherungsart', titel: 'Sicherungsart', wert: (k) => k.sicherungsart,
      inhalt: (k) => (k.sicherungsart
        ? <>{k.sicherungsart}{k.sicherungsartBezeichnung && <span className="schwach"> · {k.sicherungsartBezeichnung}</span>}</>
        : <>—</>),
    },
    { schluessel: 'alternativen', titel: 'Alternativen', wert: (k) => k.alternativen ?? '' },
    { schluessel: 'norm', titel: 'Norm', wert: (k) => [k.norm, k.tabellenzitat].filter(Boolean).join(' · ') },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
      wert: (k) => herkunft(k.herkunft).text,
      inhalt: (k) => <StatusBadge {...herkunft(k.herkunft)} klein />,
    },
    {
      schluessel: 'pruefstand', titel: 'Prüfstand', art: 'status',
      wert: (k) => pruefstand(k.geprueft).text,
      inhalt: (k) => <StatusBadge {...pruefstand(k.geprueft)} klein />,
    },
    { schluessel: 'bemerkung', titel: 'Bemerkung', wert: (k) => k.bemerkung ?? '' },
  ];

  return (
    <ContentSection
      id="absicherung-katalog"
      titel="Katalog Gegenstandsart → Flüssigkeitskategorie"
      meta={`${formatiereZahl(katalog.length, 0)} Zeilen`}
      beschreibung={'Woraus die Einstufung folgt - die Vorgabe dieser Seite (S1.6). Solange der Katalog nicht '
        + 'freigegeben ist (SAN-07), ist jede Sicherungsart oben vorläufig.'}
      nackt
    >
      <DataTable
        name="Katalog"
        spalten={spalten}
        zeilen={katalog}
        schluessel={(k) => k.id}
        ausfuhr
        ausfuhrDateiname="sanitaer-absicherung-katalog"
        dicht
        leer={<EmptyState klein titel="Der Katalog ist leer." />}
      />
    </ContentSection>
  );
}
